import { useState, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { CommandPalette } from '@/components/command/CommandPalette'
import { CreateProjectModal } from '@/features/projects/CreateProjectModal'
import { Sidebar } from './Sidebar'
import { Menu, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/stores/useAuthStore'
import { cn } from '@/lib/utils'
import { LandingPage } from '@/pages/LandingPage'

// Local storage key for sidebar state
const SIDEBAR_COLLAPSED_KEY = 'dsp-sidebar-collapsed'

export function AppLayout() {
  const { fetchUser, isLoading, isAuthenticated } = useAuthStore()
  const location = useLocation()
  const [createProjectOpen, setCreateProjectOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Initialize collapsed state from localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
    return stored === 'true'
  })

  // Persist sidebar state to localStorage
  const handleSidebarToggle = () => {
    setSidebarCollapsed((prev) => {
      const newValue = !prev
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newValue))
      return newValue
    })
  }

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [mobileMenuOpen])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  // Fetch user on mount
  useEffect(() => {
    fetchUser()
  }, [fetchUser])

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Show landing page for unauthenticated users
  if (!isAuthenticated) {
    return <LandingPage />
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none"
      >
        Skip to main content
      </a>

      {/* Mobile Menu Backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <button
        onClick={() => setMobileMenuOpen(true)}
        className={cn(
          'fixed bottom-6 left-6 z-40 md:hidden',
          'flex h-14 w-14 items-center justify-center',
          'rounded-full bg-monokai-pink text-white shadow-lg',
          'hover:bg-monokai-pink/90 active:scale-95 transition-all',
          mobileMenuOpen && 'opacity-0 pointer-events-none'
        )}
        aria-label="Open navigation menu"
        aria-expanded={mobileMenuOpen}
        aria-controls="mobile-sidebar"
      >
        <Menu className="h-6 w-6" />
      </button>

      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />

      {/* Main Content Area */}
      <div
        className={cn(
          'min-h-screen transition-all duration-300 ease-in-out',
          'md:ml-16',
          !sidebarCollapsed && 'md:ml-56'
        )}
      >
        {/* Main Content */}
        <main id="main-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      <CommandPalette onOpenCreateProject={() => setCreateProjectOpen(true)} />
      <CreateProjectModal open={createProjectOpen} onOpenChange={setCreateProjectOpen} />

      {/* Live region for announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        id="aria-live-region"
      />
    </div>
  )
}
